import { useState, useRef, useEffect, useMemo, forwardRef, useImperativeHandle } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Layout, ZoomIn, ZoomOut, Maximize2, Minimize2, Loader2, RefreshCw, RotateCcw, Plus, Minus, Download } from "lucide-react";

const SVG_W = 800;
const SVG_H = 600;
const MARGIN = 40;
const HANDLE_SIZE = 8;

const BUILDING_COLORS = {
  cafe: "#f59e0b",
  store: "#7c3aed",
  car_wash: "#0ea5e9",
  restrooms: "#dc2626",
};

// Returns {fill, stroke} based on charger type and power kW
function getChargerColors(type, powerKw) {
  const isAC = !!(type && type.toLowerCase().includes("ac"));
  if (isAC) return { fill: "#16a34a", stroke: "#14532d" };
  const p = powerKw || 0;
  if (p >= 150) return { fill: "#7c3aed", stroke: "#6d28d9" }; // violet — supercharger
  if (p >= 120) return { fill: "#4f46e5", stroke: "#3730a3" }; // indigo
  if (p >= 60)  return { fill: "#2563eb", stroke: "#1e40af" }; // blue
  if (p >= 30)  return { fill: "#0ea5e9", stroke: "#0369a1" }; // sky
  return { fill: "#2563eb", stroke: "#1e40af" };                // default DC blue
}

function getBuildingColor(btype) {
  return BUILDING_COLORS[btype] || "#d1d5db";
}

function isDCFast(type) {
  if (!type) return true;
  return !type.toLowerCase().includes("ac");
}

// Returns true when two meter-coordinate bounding boxes intersect
function boxesOverlap(a, b) {
  return !(a.x + a.w <= b.x || b.x + b.w <= a.x || a.y + a.h <= b.y || b.y + b.h <= a.y);
}

// Shift buildings that overlap chargers/parking to the right side of the site
function autoFixOverlaps(chargers, parking, buildings, siteW, siteH) {
  const fixed = [
    ...chargers.map(c => ({
      x: c.x - (c.bay_width ?? 3.5) / 2,
      y: c.y,
      w: c.bay_width ?? 3.5,
      h: c.bay_length ?? 6,
    })),
    ...parking.map(p => ({
      x: p.x,
      y: p.y,
      w: p.width ?? 2.5,
      h: p.height ?? 5,
    })),
  ];

  const result = {};
  const placed = [...fixed];
  let nextY = siteH;

  for (const [btype, bdata] of Object.entries(buildings)) {
    if (!bdata) { result[btype] = null; continue; }
    const { width: w, height: h } = bdata;
    const orig = { x: bdata.x, y: bdata.y, w, h };

    if (!placed.some(o => boxesOverlap(orig, o))) {
      result[btype] = bdata;
      placed.push(orig);
      continue;
    }

    // Stack overlapping buildings vertically on the right edge of the site
    nextY -= h + 1;
    const tx = Math.max(0, siteW - w - 1);
    const ty = Math.max(0, nextY);
    const moved = { x: tx, y: ty, w, h };

    if (!placed.some(o => boxesOverlap(moved, o))) {
      result[btype] = { ...bdata, x: tx, y: ty };
      placed.push(moved);
    } else {
      result[btype] = bdata; // couldn't resolve — keep original
      placed.push(orig);
    }
  }
  return result;
}

// Returns 8 resize handle descriptors for any SVG rect
function getResizeHandles(r) {
  return [
    { corner: "tl", x: r.x,           y: r.y,           cursor: "nw-resize" },
    { corner: "tr", x: r.x + r.w,     y: r.y,           cursor: "ne-resize" },
    { corner: "bl", x: r.x,           y: r.y + r.h,     cursor: "sw-resize" },
    { corner: "br", x: r.x + r.w,     y: r.y + r.h,     cursor: "se-resize" },
    { corner: "n",  x: r.x + r.w / 2, y: r.y,           cursor: "n-resize"  },
    { corner: "s",  x: r.x + r.w / 2, y: r.y + r.h,     cursor: "s-resize"  },
    { corner: "e",  x: r.x + r.w,     y: r.y + r.h / 2, cursor: "e-resize"  },
    { corner: "w",  x: r.x,           y: r.y + r.h / 2, cursor: "w-resize"  },
  ];
}

const SiteLayoutMap = forwardRef(function SiteLayoutMap({ layout, siteInputs, isRerunning, onRerun, onPositionsChange }, ref) {
  const [zoom, setZoom] = useState(1.0);
  const [hoveredEl, setHoveredEl] = useState(null);
  const [hoveredBuilding, setHoveredBuilding] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [positions, setPositions] = useState(null);
  const [drag, setDrag] = useState(null);
  const [snapEnabled, setSnapEnabled] = useState(true);

  // Display unit — mirrors what the user chose in the wizard
  const displayUnit = siteInputs?.land_unit || "meters";
  const isFt = displayUnit === "feet";
  const isYd = displayUnit === "yards";
  const unitLabel   = isFt ? "ft" : isYd ? "yd" : "m";
  const unitLabelSq = isFt ? "ft²" : isYd ? "yd²" : "m²";
  const mToDisplay  = (m) => isFt ? m / 0.3048 : isYd ? m / 0.9144 : m;
  const m2ToDisplay = (m2) => isFt ? m2 / 0.0929 : isYd ? m2 / 0.8361 : m2;
  const fmtLen = (m) => mToDisplay(m).toFixed(isFt ? 0 : 1);
  const fmtArea = (m2) => m2ToDisplay(m2).toFixed(0);
  // Tick step in metres that gives neat round numbers in the display unit
  const tickStepM = isFt ? 10 * 0.3048 : isYd ? 5 * 0.9144 : 5;
  const tickDisplayStep = isFt ? 10 : 5; // step size in display units

  const containerRef = useRef(null);
  const scrollRef = useRef(null);
  const svgRef = useRef(null);

  // Coordinate helpers
  const siteW = layout?.dimensions?.total_length || siteInputs?.land_length || 100;
  const siteH = layout?.dimensions?.total_width || siteInputs?.land_width || 100;
  const scaleX = (SVG_W - 2 * MARGIN) / siteW;
  const scaleY = (SVG_H - 2 * MARGIN) / siteH;

  const toSvgX = (mx) => MARGIN + mx * scaleX;
  const toSvgY = (my) => MARGIN + (siteH - my) * scaleY;
  const toSvgRect = (mx, my, mw, mh) => ({
    x: toSvgX(mx),
    y: toSvgY(my + mh),
    w: mw * scaleX,
    h: mh * scaleY,
  });

  // Build initial positions (with overlap auto-fix) whenever layout changes
  const initialPositions = useMemo(() => {
    if (!layout) return null;
    const sW = layout?.dimensions?.total_length || siteInputs?.land_length || 100;
    const sH = layout?.dimensions?.total_width || siteInputs?.land_width || 100;
    const chargers = layout.charger_arrangement?.positions || [];
    const parking = layout.parking?.positions || [];
    const buildings = layout.buildings || {};
    const solar = layout.solar?.position || null;
    return {
      chargers: chargers.map(c => ({ ...c })),
      parking: parking.map(p => ({ ...p })),
      buildings: autoFixOverlaps(chargers, parking, buildings, sW, sH),
      solar: solar ? { ...solar } : null,
    };
  }, [layout, siteInputs]);

  useEffect(() => {
    if (!initialPositions) return;
    setPositions(prev => {
      // First load — use computed positions as-is
      if (!prev) return initialPositions;

      // Smart merge: preserve user-placed positions; only add/remove changed elements
      // Chargers — keep position by ID, update type/power, append brand-new ones
      const prevById = Object.fromEntries(prev.chargers.map(c => [c.id, c]));
      const mergedChargers = initialPositions.chargers.map(c =>
        prevById[c.id]
          ? { ...c, x: prevById[c.id].x, y: prevById[c.id].y }  // keep user position
          : c  // new charger — use computed position
      );

      // Buildings — keep user's position/size if building still present in new layout
      const mergedBuildings = {};
      for (const [key, bdata] of Object.entries(initialPositions.buildings || {})) {
        mergedBuildings[key] = (bdata && prev.buildings?.[key]) ? prev.buildings[key] : bdata;
      }

      // Solar — keep user's resized/repositioned canopy; use computed only when newly toggled on
      const mergedSolar = prev.solar && initialPositions.solar
        ? prev.solar                 // already exists — preserve user changes
        : initialPositions.solar;    // newly added (or removed → null)

      return {
        chargers: mergedChargers,
        parking:  prev.parking,      // always keep user's parking layout
        buildings: mergedBuildings,
        solar: mergedSolar,
      };
    });
  }, [initialPositions]);

  // Notify parent whenever positions change (used by wizard to track frozen layout)
  useEffect(() => {
    if (onPositionsChange && positions) onPositionsChange(positions);
  }, [positions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cancel drag when pointer released anywhere (mouse or touch)
  useEffect(() => {
    const up = () => setDrag(null);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
    };
  }, []);

  // Prevent page scroll while dragging on touch devices
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const prevent = (e) => { if (e.cancelable) e.preventDefault(); };
    svg.addEventListener("touchmove", prevent, { passive: false });
    return () => svg.removeEventListener("touchmove", prevent);
  }, []);

  // Convert client coords → SVG viewport coords (accounts for zoom + scroll)
  const toSvgCoords = (clientX, clientY) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return { x: (clientX - rect.left) / zoom, y: (clientY - rect.top) / zoom };
  };

  // Extract clientX/Y from either a mouse or touch event
  const getClient = (e) => {
    if (e.touches && e.touches.length > 0) return { clientX: e.touches[0].clientX, clientY: e.touches[0].clientY };
    if (e.changedTouches && e.changedTouches.length > 0) return { clientX: e.changedTouches[0].clientX, clientY: e.changedTouches[0].clientY };
    return { clientX: e.clientX, clientY: e.clientY };
  };

  const snap = (v) => snapEnabled ? Math.round(v * 2) / 2 : Math.round(v * 10) / 10;

  // Begin drag — works for both mouse and touch
  const startDrag = (e, type, id, elX, elY, elW, elH) => {
    e.preventDefault();
    e.stopPropagation();
    const { clientX, clientY } = getClient(e);
    const { x, y } = toSvgCoords(clientX, clientY);
    setDrag({ type, id, svgX0: x, svgY0: y, elX0: elX, elY0: elY, elW, elH });
    setHoveredEl(null);
  };

  // Begin solar canopy resize
  const startSolarResize = (e, corner, solar) => {
    e.preventDefault();
    e.stopPropagation();
    const { clientX, clientY } = getClient(e);
    const { x, y } = toSvgCoords(clientX, clientY);
    setDrag({
      type: "solar-resize",
      corner,
      svgX0: x, svgY0: y,
      solarX0: solar.x, solarY0: solar.y,
      solarW0: solar.width, solarH0: solar.height,
    });
    setHoveredEl(null);
  };

  // Begin amenity building resize
  const startBuildingResize = (e, corner, btype, bdata) => {
    e.preventDefault();
    e.stopPropagation();
    const { clientX, clientY } = getClient(e);
    const { x, y } = toSvgCoords(clientX, clientY);
    setDrag({
      type: "building-resize",
      id: btype,
      corner,
      svgX0: x, svgY0: y,
      bX0: bdata.x, bY0: bdata.y,
      bW0: bdata.width, bH0: bdata.height,
    });
    setHoveredEl(null);
    setHoveredBuilding(btype);
  };

  const handleMove = (e) => {
    if (!drag) return;
    const { clientX, clientY } = getClient(e);
    const { x: svgX, y: svgY } = toSvgCoords(clientX, clientY);
    // dx = metres moved right, dy = metres moved up
    const dx = (svgX - drag.svgX0) / scaleX;
    const dy = -(svgY - drag.svgY0) / scaleY;

    setPositions(prev => {
      if (!prev) return prev;
      const { type, id, elW } = drag;

      // ── Solar canopy resize ───────────────────────────────────────────────
      if (type === "solar-resize") {
        if (!prev.solar) return prev;
        const { corner, solarX0, solarY0, solarW0, solarH0 } = drag;
        let nx = solarX0, ny = solarY0, nw = solarW0, nh = solarH0;

        // tl/tr/bl/br = SVG corners; n/s/e/w = edge midpoints
        // "tl" in SVG space = top of screen = higher meter-y = top-left in meter space
        if (corner === "tl") {
          nx = snap(solarX0 + dx);
          nw = Math.max(2, snap(solarW0 - dx));
          nh = Math.max(2, snap(solarH0 + dy));
        } else if (corner === "tr") {
          nw = Math.max(2, snap(solarW0 + dx));
          nh = Math.max(2, snap(solarH0 + dy));
        } else if (corner === "bl") {
          nx = snap(solarX0 + dx);
          nw = Math.max(2, snap(solarW0 - dx));
          ny = snap(solarY0 + dy);
          nh = Math.max(2, snap(solarH0 - dy));
        } else if (corner === "br") {
          nw = Math.max(2, snap(solarW0 + dx));
          ny = snap(solarY0 + dy);
          nh = Math.max(2, snap(solarH0 - dy));
        } else if (corner === "n") {
          nh = Math.max(2, snap(solarH0 + dy));
        } else if (corner === "s") {
          ny = snap(solarY0 + dy);
          nh = Math.max(2, snap(solarH0 - dy));
        } else if (corner === "e") {
          nw = Math.max(2, snap(solarW0 + dx));
        } else if (corner === "w") {
          nx = snap(solarX0 + dx);
          nw = Math.max(2, snap(solarW0 - dx));
        }

        // Clamp to site bounds
        nx = Math.max(0, nx);
        ny = Math.max(0, ny);
        nw = Math.min(siteW - nx, nw);
        nh = Math.min(siteH - ny, nh);

        return { ...prev, solar: { ...prev.solar, x: nx, y: ny, width: nw, height: nh } };
      }

      // ── Amenity building resize ───────────────────────────────────────────
      if (type === "building-resize") {
        const { corner, bX0, bY0, bW0, bH0, id: btype } = drag;
        if (!prev.buildings?.[btype]) return prev;
        let nx = bX0, ny = bY0, nw = bW0, nh = bH0;
        const minSize = 2;
        if (corner === "tl")      { nx = snap(bX0 + dx); nw = Math.max(minSize, snap(bW0 - dx)); nh = Math.max(minSize, snap(bH0 + dy)); }
        else if (corner === "tr") { nw = Math.max(minSize, snap(bW0 + dx)); nh = Math.max(minSize, snap(bH0 + dy)); }
        else if (corner === "bl") { nx = snap(bX0 + dx); nw = Math.max(minSize, snap(bW0 - dx)); ny = snap(bY0 + dy); nh = Math.max(minSize, snap(bH0 - dy)); }
        else if (corner === "br") { nw = Math.max(minSize, snap(bW0 + dx)); ny = snap(bY0 + dy); nh = Math.max(minSize, snap(bH0 - dy)); }
        else if (corner === "n")  { nh = Math.max(minSize, snap(bH0 + dy)); }
        else if (corner === "s")  { ny = snap(bY0 + dy); nh = Math.max(minSize, snap(bH0 - dy)); }
        else if (corner === "e")  { nw = Math.max(minSize, snap(bW0 + dx)); }
        else if (corner === "w")  { nx = snap(bX0 + dx); nw = Math.max(minSize, snap(bW0 - dx)); }
        nx = Math.max(0, nx); ny = Math.max(0, ny);
        nw = Math.min(siteW - nx, nw); nh = Math.min(siteH - ny, nh);
        return {
          ...prev,
          buildings: { ...prev.buildings, [btype]: { ...prev.buildings[btype], x: nx, y: ny, width: nw, height: nh } },
        };
      }

      // ── Regular element drag ──────────────────────────────────────────────
      const newX = snap(Math.max(0, Math.min(siteW - drag.elW, drag.elX0 + dx)));
      const newY = snap(Math.max(0, Math.min(siteH - drag.elH, drag.elY0 + dy)));

      if (type === "charger") {
        return {
          ...prev,
          chargers: prev.chargers.map(c =>
            c.id === id ? { ...c, x: newX + elW / 2, y: newY } : c
          ),
        };
      }
      if (type === "parking") {
        return {
          ...prev,
          parking: prev.parking.map(p => p.id === id ? { ...p, x: newX, y: newY } : p),
        };
      }
      if (type === "building") {
        return {
          ...prev,
          buildings: { ...prev.buildings, [id]: { ...prev.buildings[id], x: newX, y: newY } },
        };
      }
      if (type === "solar" && prev.solar) {
        return { ...prev, solar: { ...prev.solar, x: newX, y: newY } };
      }
      return prev;
    });
  };

  const handleEnd = () => setDrag(null);

  const handleHover = (el, event) => {
    if (drag) return;
    const rect = containerRef.current?.getBoundingClientRect();
    setHoveredEl({
      ...el,
      tipX: event.clientX - (rect?.left ?? 0) + 14,
      tipY: event.clientY - (rect?.top ?? 0) - 44,
    });
  };
  const clearHover = () => setHoveredEl(null);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  // Add a new parking bay positioned after the last existing one
  const addParkingBay = () => {
    setPositions(prev => {
      if (!prev) return prev;
      const existing = prev.parking;
      const pw = 2.5, ph = 5;
      let newX = 0, newY = 0;
      if (existing.length > 0) {
        const lastP = existing[existing.length - 1];
        newX = snap(lastP.x + (lastP.width ?? pw) + 0.5);
        newY = lastP.y;
        if (newX + pw > siteW) {
          newX = 0;
          newY = snap(lastP.y + (lastP.height ?? ph) + 0.5);
        }
      }
      newX = Math.max(0, Math.min(siteW - pw, newX));
      newY = Math.max(0, Math.min(siteH - ph, newY));
      const newId = `P${existing.length + 1}`;
      return { ...prev, parking: [...existing, { id: newId, x: newX, y: newY, width: pw, height: ph }] };
    });
  };

  // Remove the last parking bay
  const removeParkingBay = () => {
    setPositions(prev => {
      if (!prev || prev.parking.length === 0) return prev;
      return { ...prev, parking: prev.parking.slice(0, -1) };
    });
  };

  // Export the SVG canvas as a PNG image
  const exportPNG = () => {
    const svgEl = svgRef.current;
    if (!svgEl) return;
    const serializer = new XMLSerializer();
    const svgStr = serializer.serializeToString(svgEl);
    const blob = new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" });
    const url  = URL.createObjectURL(blob);
    const img  = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width  = SVG_W * 2;
      canvas.height = SVG_H * 2;
      const ctx = canvas.getContext("2d");
      ctx.scale(2, 2);
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, SVG_W, SVG_H);
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      const a = document.createElement("a");
      a.href     = canvas.toDataURL("image/png");
      a.download = `ev-layout-${Date.now()}.png`;
      a.click();
    };
    img.src = url;
  };

  // Expose getSvgString() to parent via ref — must be before any early returns
  useImperativeHandle(ref, () => ({
    getSvgString() {
      const svgEl = svgRef.current;
      if (!svgEl) return '';
      const serializer = new XMLSerializer();
      let svgStr = serializer.serializeToString(svgEl);
      // Normalise to fixed 800×600 regardless of current zoom level
      svgStr = svgStr.replace(/\bwidth="[^"]*"/, 'width="800"').replace(/\bheight="[^"]*"/, 'height="600"');
      return svgStr;
    },
  }));

  // ── Empty state ─────────────────────────────────────────────────────────────
  if (!layout) {
    return (
      <Card className="border-dashed border-slate-700">
        <CardContent className="p-12 text-center">
          <Layout className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="font-semibold text-white mb-2">No Layout Generated</h3>
          <p className="text-slate-500 mb-4">Run analysis to generate a site layout</p>
          <Button onClick={onRerun} disabled={isRerunning} className="bg-primary hover:bg-blue-600">
            {isRerunning
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <RefreshCw className="w-4 h-4 mr-2" />}
            Generate Layout
          </Button>
        </CardContent>
      </Card>
    );
  }

  const eff = positions || initialPositions;
  if (!eff) return null;

  const chargerPositions = eff.chargers;
  const parkingPositions = eff.parking;
  const buildings = eff.buildings;
  const solarPos = eff.solar;
  const trafficFlow = layout.traffic_flow || {};
  const hasPositionData = chargerPositions.length > 0 || parkingPositions.length > 0;

  // Live solar kWp scaled proportionally with current canopy area
  const origSolarArea =
    layout.solar?.area_sqm ||
    ((layout.solar?.position?.width ?? 0) * (layout.solar?.position?.height ?? 0));
  const computedSolarKwp =
    solarPos && layout.solar?.estimated_kWp && origSolarArea > 0
      ? parseFloat(
          ((layout.solar.estimated_kWp / origSolarArea) * (solarPos.width * solarPos.height)).toFixed(1)
        )
      : layout.solar?.estimated_kWp;

  // Overlap detection — builds a set of keys for elements that currently overlap
  const allBoxes = [
    ...chargerPositions.map(c => {
      const bw = c.bay_width ?? 3.5, bl = c.bay_length ?? 6;
      return { key: `charger:${c.id}`, x: c.x - bw / 2, y: c.y, w: bw, h: bl };
    }),
    ...parkingPositions.map(p => ({
      key: `parking:${p.id}`, x: p.x, y: p.y, w: p.width ?? 2.5, h: p.height ?? 5,
    })),
    ...Object.entries(buildings)
      .filter(([, b]) => b)
      .map(([bt, b]) => ({ key: `building:${bt}`, x: b.x, y: b.y, w: b.width, h: b.height })),
    ...(solarPos
      ? [{ key: "solar:canopy", x: solarPos.x, y: solarPos.y, w: solarPos.width, h: solarPos.height }]
      : []),
  ];
  const overlapSet = new Set();
  for (let i = 0; i < allBoxes.length; i++) {
    for (let j = i + 1; j < allBoxes.length; j++) {
      if (boxesOverlap(allBoxes[i], allBoxes[j])) {
        overlapSet.add(allBoxes[i].key);
        overlapSet.add(allBoxes[j].key);
      }
    }
  }
  const overlapCount = overlapSet.size;

  // Grid — ticks in metres, labels in display unit
  const gridStepX = tickStepM * scaleX;
  const gridStepY = tickStepM * scaleY;
  const xTicks = Array.from({ length: Math.floor(siteW / tickStepM) + 1 }, (_, i) => ({
    m: i * tickStepM, label: String(i * tickDisplayStep),
  }));
  const yTicks = Array.from({ length: Math.floor(siteH / tickStepM) + 1 }, (_, i) => ({
    m: i * tickStepM, label: String(i * tickDisplayStep),
  }));

  // Entry / exit arrows
  const entryY = trafficFlow.entry ? toSvgY(trafficFlow.entry.y) : SVG_H / 2;
  const exitY = trafficFlow.exit ? toSvgY(trafficFlow.exit.y) : SVG_H / 2;
  const arrowSize = 9;

  // Scale bar — 10m or 10ft or 5yd reference
  const scaleBarRef = isFt ? 10 * 0.3048 : isYd ? 5 * 0.9144 : 10; // metres
  const scaleBarLabel = isFt ? "10ft" : isYd ? "5yd" : "10m";
  const scaleBarPx = scaleBarRef * scaleX;
  const sbX = MARGIN + 6;
  const sbY = SVG_H - 14;

  // Legend — charger entries are dynamic based on unique types present in the layout
  const seenChargerKeys = new Set();
  const chargerLegendEntries = [];
  for (const c of chargerPositions) {
    const key = `${c.type}_${c.power_kw ?? 0}`;
    if (!seenChargerKeys.has(key)) {
      seenChargerKeys.add(key);
      const { fill } = getChargerColors(c.type, c.power_kw);
      const isAC = !!(c.type && c.type.toLowerCase().includes("ac"));
      const label = c.power_kw
        ? `${c.power_kw}kW ${isAC ? "AC" : "DC"} Charger`
        : (isAC ? "AC Slow Charger" : "DC Fast Charger");
      chargerLegendEntries.push({ color: fill, label });
    }
  }
  const legendItems = [
    ...chargerLegendEntries,
    { color: "#00C853", label: "Solar Canopy", opacity: 0.25, dashed: true },
    { color: "#e2e8f0", label: "Parking Bay", stroke: "#94a3b8" },
    { color: "#f59e0b", label: "Amenity Building" },
  ];
  const legendX = SVG_W - MARGIN - 169;
  const legendY = SVG_H - MARGIN - legendItems.length * 18 - 22;

  // Stats
  const unitCount = new Set(chargerPositions.map(c => c.pair_index).filter(p => p != null)).size
    || chargerPositions.length;
  const dcCount = chargerPositions.filter(c => isDCFast(c.type)).length;
  const acCount = chargerPositions.length - dcCount;
  const buildingCount = Object.values(buildings).filter(Boolean).length;

  // Area calculations for side panel
  const dcChargers = chargerPositions.filter(c => isDCFast(c.type));
  const acChargers = chargerPositions.filter(c => !isDCFast(c.type));
  const dcTotalArea = dcChargers.reduce((sum, c) => sum + (c.bay_width ?? 3.5) * (c.bay_length ?? 6), 0);
  const acTotalArea = acChargers.reduce((sum, c) => sum + (c.bay_width ?? 3.5) * (c.bay_length ?? 6), 0);
  const parkingTotalArea = parkingPositions.reduce((sum, p) => sum + (p.width ?? 2.5) * (p.height ?? 5), 0);
  const solarArea = solarPos ? solarPos.width * solarPos.height : 0;
  const buildingEntries = Object.entries(buildings).filter(([, b]) => b);
  const totalBuildingArea = buildingEntries.reduce((sum, [, b]) => sum + b.width * b.height, 0);
  const siteArea = siteW * siteH;
  const usedArea = dcTotalArea + acTotalArea + parkingTotalArea + solarArea + totalBuildingArea;

  return (
    <div ref={containerRef} className="relative" style={{ userSelect: drag ? "none" : "auto" }}>
      {/* ── Stats bar ───────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-4 mb-3 px-1 flex-wrap text-sm">
        <span className="font-medium text-slate-300">
          {unitCount} Charger Unit{unitCount !== 1 ? "s" : ""}
          <span className="text-slate-400 font-normal text-xs ml-1">({chargerPositions.length} bays)</span>
        </span>
        {dcCount > 0 && (
          <span className="flex items-center gap-1">
            <span className="inline-block w-2.5 h-2.5 rounded-sm bg-blue-700" />
            <span className="text-slate-400 text-xs">{dcCount / 2 | 0} DC</span>
          </span>
        )}
        {acCount > 0 && (
          <span className="flex items-center gap-1">
            <span className="inline-block w-2.5 h-2.5 rounded-sm bg-green-700" />
            <span className="text-slate-400 text-xs">{acCount / 2 | 0} AC</span>
          </span>
        )}
        <span className="text-slate-300">|</span>
        <span className="font-medium text-slate-300">{parkingPositions.length} Parking <span className="text-slate-400 font-normal text-xs">(2/charger)</span></span>
        <span className="text-slate-300">|</span>
        <span className="font-medium text-slate-300">{buildingCount} Buildings</span>
        {solarPos && (
          <>
            <span className="text-slate-300">|</span>
            <span className="font-medium text-slate-300">{computedSolarKwp} kWp Solar</span>
          </>
        )}
        {overlapCount > 0 && (
          <span className="ml-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
            ⚠ {overlapCount} overlapping elements — drag to resolve
          </span>
        )}
      </div>

      {/* ── Controls toolbar ────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Button
          variant="outline" size="sm"
          onClick={() => setZoom(z => Math.max(0.5, parseFloat((z - 0.25).toFixed(2))))}
        >
          <ZoomOut className="w-4 h-4" />
        </Button>
        <span className="text-sm font-mono w-12 text-center select-none">
          {Math.round(zoom * 100)}%
        </span>
        <Button
          variant="outline" size="sm"
          onClick={() => setZoom(z => Math.min(3.0, parseFloat((z + 0.25).toFixed(2))))}
        >
          <ZoomIn className="w-4 h-4" />
        </Button>
        <Button variant="outline" size="sm" onClick={() => setZoom(1.0)}>1:1</Button>
        <Button variant="outline" size="sm" onClick={toggleFullscreen}>
          {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </Button>
        <div className="w-px h-5 bg-slate-700 mx-1" />
        <div className="relative group">
          <Button
            variant={snapEnabled ? "default" : "outline"}
            size="sm"
            onClick={() => setSnapEnabled(s => !s)}
          >
            Snap {snapEnabled ? "ON" : "OFF"}
          </Button>
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 bg-slate-900 text-white text-xs rounded px-3 py-2 shadow-xl z-50 hidden group-hover:block pointer-events-none">
            <p className="font-semibold mb-1">Snap to Grid</p>
            <p className="text-slate-300">
              <span className="font-medium text-white">ON:</span> Elements snap to the nearest 0.5m — keeps chargers and buildings neatly aligned.
            </p>
            <p className="text-slate-300 mt-1">
              <span className="font-medium text-white">OFF:</span> Fine-grained 0.1m positioning for precise placement in tight spaces.
            </p>
            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900" />
          </div>
        </div>
        <Button
          variant="outline" size="sm"
          onClick={() => setPositions(initialPositions)}
          title="Reset to auto-fixed positions"
        >
          <RotateCcw className="w-4 h-4 mr-1" />
          Reset
        </Button>
        <div className="w-px h-5 bg-slate-700 mx-1" />
        {/* Parking bay count control */}
        <div className="flex items-center gap-1.5">
          <span className="text-sm text-slate-400 font-medium">Parking Bays:</span>
          <Button
            variant="outline" size="sm"
            onClick={removeParkingBay}
            disabled={parkingPositions.length === 0}
            className="w-7 h-7 p-0"
          >
            <Minus className="w-3 h-3" />
          </Button>
          <span className="text-sm font-mono w-7 text-center font-semibold text-slate-300 select-none">
            {parkingPositions.length}
          </span>
          <Button
            variant="outline" size="sm"
            onClick={addParkingBay}
            className="w-7 h-7 p-0"
          >
            <Plus className="w-3 h-3" />
          </Button>
        </div>
        <div className="w-px h-5 bg-slate-700 mx-1" />
        <Button variant="outline" size="sm" onClick={exportPNG} title="Export as PNG">
          <Download className="w-4 h-4 mr-1" /> PNG
        </Button>
        {!hasPositionData && (
          <span className="ml-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded">
            Legacy layout — exact positions unavailable
          </span>
        )}
      </div>

      {/* Drag instructions */}
      <p className="text-xs text-slate-500 mb-2 select-none">
        Drag any element to reposition. Hover over buildings or the solar canopy to reveal resize handles.
        {snapEnabled && " Snapping to 0.5m grid."}
      </p>

      {/* ── Main area: SVG editor + area info panel ──────────────────────────── */}
      <div className="flex flex-col lg:flex-row gap-3 items-start">

        {/* SVG map */}
        <div className="flex-1 min-w-0 w-full overflow-x-auto">
          <div
            ref={scrollRef}
            className="overflow-auto border border-slate-700 rounded-lg bg-slate-900"
            style={{ maxHeight: isFullscreen ? "100vh" : 640 }}
          >
            <svg
              ref={svgRef}
              width={SVG_W * zoom}
              height={SVG_H * zoom}
              viewBox={`0 0 ${SVG_W} ${SVG_H}`}
              style={{ display: "block", cursor: drag ? "grabbing" : "default", touchAction: "none" }}
              onMouseMove={handleMove}
              onMouseUp={handleEnd}
              onMouseLeave={clearHover}
              onTouchMove={handleMove}
              onTouchEnd={handleEnd}
            >
              <defs>
                <pattern
                  id="evgrid" x={MARGIN} y={MARGIN}
                  width={gridStepX} height={gridStepY}
                  patternUnits="userSpaceOnUse"
                >
                  <path
                    d={`M ${gridStepX} 0 L 0 0 0 ${gridStepY}`}
                    fill="none" stroke="#1e3a5f" strokeWidth={0.5}
                  />
                </pattern>
                <filter id="charger-shadow" x="-25%" y="-25%" width="150%" height="150%">
                  <feDropShadow dx="0" dy="1.5" stdDeviation="2.5" floodColor="#00000044" />
                </filter>
              </defs>

              {/* Background */}
              <rect x={0} y={0} width={SVG_W} height={SVG_H} fill="#0f172a" />
              <rect x={MARGIN} y={MARGIN} width={SVG_W - 2 * MARGIN} height={SVG_H - 2 * MARGIN}
                fill="url(#evgrid)" />
              <rect x={MARGIN} y={MARGIN} width={SVG_W - 2 * MARGIN} height={SVG_H - 2 * MARGIN}
                fill="none" stroke="#374151" strokeWidth={2} />

              {/*
                x-excluded="true" — prevents the dev-mode babel-metadata-plugin from
                wrapping {expression} children in <span>, which breaks SVG namespace.
              */}

              {/* LAYER 1 — Solar canopy */}
              <g x-excluded="true">
                {solarPos && (() => {
                  const r = toSvgRect(solarPos.x, solarPos.y, solarPos.width, solarPos.height);
                  const isOverlap = overlapSet.has("solar:canopy");
                  const handles = getResizeHandles(r);
                  return (
                    <g x-excluded="true">
                      <rect
                        x={r.x} y={r.y} width={r.w} height={r.h}
                        fill="#00C853" fillOpacity={0.18}
                        stroke={isOverlap ? "#f97316" : "#00C853"}
                        strokeWidth={isOverlap ? 2 : 1.5}
                        strokeDasharray="8 4" rx={4}
                        style={{ cursor: "grab", touchAction: "none" }}
                        onMouseDown={(e) => startDrag(e, "solar", "canopy", solarPos.x, solarPos.y, solarPos.width, solarPos.height)}
                        onTouchStart={(e) => startDrag(e, "solar", "canopy", solarPos.x, solarPos.y, solarPos.width, solarPos.height)}
                        onMouseEnter={(e) => handleHover({
                          label: "Solar Canopy",
                          details: [
                            `${computedSolarKwp} kWp capacity`,
                            `Area: ${fmtArea(solarPos.width * solarPos.height)} ${unitLabelSq}`,
                            `${fmtLen(solarPos.width)}${unitLabel} × ${fmtLen(solarPos.height)}${unitLabel}`,
                            "Drag body to move · handles to resize",
                          ],
                        }, e)}
                        onMouseLeave={clearHover}
                      />
                      {r.w > 60 && r.h > 16 && (
                        <text x-excluded="true"
                          x={r.x + r.w / 2} y={r.y + r.h / 2 + 4}
                          textAnchor="middle" fill="#166534" fontSize={11} fontWeight="600"
                          style={{ pointerEvents: "none" }}>
                          ☀ Solar {computedSolarKwp} kWp
                        </text>
                      )}
                      {/* Resize handles */}
                      {handles.map(h => (
                        <rect
                          key={h.corner}
                          x={h.x - HANDLE_SIZE / 2} y={h.y - HANDLE_SIZE / 2}
                          width={HANDLE_SIZE} height={HANDLE_SIZE}
                          fill="white" stroke="#16a34a" strokeWidth={1.5} rx={1}
                          style={{ cursor: h.cursor, touchAction: "none" }}
                          onMouseDown={(e) => startSolarResize(e, h.corner, solarPos)}
                          onTouchStart={(e) => startSolarResize(e, h.corner, solarPos)}
                        />
                      ))}
                    </g>
                  );
                })()}
              </g>

              {/* LAYER 2 — Parking bays */}
              <g x-excluded="true">
                {parkingPositions.map((p) => {
                  const pw = p.width ?? 2.5, ph = p.height ?? 5;
                  const r = toSvgRect(p.x, p.y, pw, ph);
                  const isOverlap = overlapSet.has(`parking:${p.id}`);
                  return (
                    <g x-excluded="true" key={p.id}>
                      <rect
                        x={r.x} y={r.y} width={r.w} height={r.h}
                        fill="#e2e8f0"
                        stroke={isOverlap ? "#f97316" : "#94a3b8"}
                        strokeWidth={isOverlap ? 1.5 : 0.8}
                        rx={1}
                        style={{ cursor: "grab", touchAction: "none" }}
                        onMouseDown={(e) => startDrag(e, "parking", p.id, p.x, p.y, pw, ph)}
                        onTouchStart={(e) => startDrag(e, "parking", p.id, p.x, p.y, pw, ph)}
                        onMouseEnter={(e) => handleHover({
                          label: p.id,
                          details: [`${pw}m × ${ph}m`, "Drag to reposition"],
                        }, e)}
                        onMouseLeave={clearHover}
                      />
                      {r.w > 12 && r.h > 10 && (
                        <text x-excluded="true"
                          x={r.x + r.w / 2} y={r.y + r.h / 2 + 3}
                          textAnchor="middle" fill="#475569" fontSize={7}
                          style={{ pointerEvents: "none" }}>
                          {p.id}
                        </text>
                      )}
                    </g>
                  );
                })}
              </g>

              {/* LAYER 3 — Amenity buildings */}
              <g x-excluded="true">
                {Object.entries(buildings).map(([btype, bdata]) => {
                  if (!bdata) return null;
                  const r = toSvgRect(bdata.x, bdata.y, bdata.width, bdata.height);
                  const color = getBuildingColor(btype);
                  const label = btype.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
                  const isOverlap = overlapSet.has(`building:${btype}`);
                  const isHov = hoveredBuilding === btype || (drag?.type === "building-resize" && drag?.id === btype);
                  const handles = isHov ? getResizeHandles(r) : [];
                  return (
                    <g x-excluded="true" key={btype}>
                      <rect
                        x={r.x} y={r.y} width={r.w} height={r.h}
                        fill={color} fillOpacity={0.82}
                        stroke={isOverlap ? "#f97316" : "#374151"}
                        strokeWidth={isOverlap ? 2.5 : 1.5}
                        rx={4}
                        style={{ cursor: "grab", touchAction: "none" }}
                        onMouseDown={(e) => startDrag(e, "building", btype, bdata.x, bdata.y, bdata.width, bdata.height)}
                        onTouchStart={(e) => startDrag(e, "building", btype, bdata.x, bdata.y, bdata.width, bdata.height)}
                        onMouseEnter={(e) => {
                          setHoveredBuilding(btype);
                          handleHover({
                            label,
                            details: [
                              `${fmtLen(bdata.width)}${unitLabel} × ${fmtLen(bdata.height)}${unitLabel}`,
                              `Area: ${fmtArea(bdata.width * bdata.height)} ${unitLabelSq}`,
                              "Drag body to move · handles to resize",
                            ],
                          }, e);
                        }}
                        onMouseLeave={() => { setHoveredBuilding(null); clearHover(); }}
                      />
                      {r.w > 20 && r.h > 14 && (
                        <text x-excluded="true"
                          x={r.x + r.w / 2} y={r.y + r.h / 2 + 4}
                          textAnchor="middle" fill="#1f2937" fontSize={10} fontWeight="600"
                          style={{ pointerEvents: "none" }}>
                          {label}
                        </text>
                      )}
                      {/* Resize handles — shown on hover */}
                      {handles.map(h => (
                        <rect
                          key={h.corner}
                          x={h.x - HANDLE_SIZE / 2} y={h.y - HANDLE_SIZE / 2}
                          width={HANDLE_SIZE} height={HANDLE_SIZE}
                          fill="white" stroke={color} strokeWidth={1.5} rx={1}
                          style={{ cursor: h.cursor, touchAction: "none" }}
                          onMouseDown={(e) => startBuildingResize(e, h.corner, btype, bdata)}
                          onTouchStart={(e) => startBuildingResize(e, h.corner, btype, bdata)}
                          onMouseEnter={() => setHoveredBuilding(btype)}
                        />
                      ))}
                    </g>
                  );
                })}
              </g>

              {/* LAYER 4 — Charger bays (topmost) */}
              <g x-excluded="true">
                {/* Group chargers into pairs by pair_index for pedestal rendering */}
                {(() => {
                  // Collect pair metadata
                  const pairMap = {};
                  for (const c of chargerPositions) {
                    const pi = c.pair_index ?? null;
                    if (pi !== null) {
                      if (!pairMap[pi]) pairMap[pi] = [];
                      pairMap[pi].push(c);
                    }
                  }

                  return chargerPositions.map((c) => {
                    const bw = c.bay_width ?? 3.5;
                    const bl = c.bay_length ?? 6;
                    const r  = toSvgRect(c.x - bw / 2, c.y, bw, bl);
                    const { fill: chargerFill, stroke: chargerStroke } = getChargerColors(c.type, c.power_kw);
                    const isOverlap = overlapSet.has(`charger:${c.id}`);
                    const pi = c.pair_index ?? null;
                    const pair = pi !== null ? pairMap[pi] : null;
                    const isLeft = c.pair_side === "left";

                    return (
                      <g x-excluded="true" key={c.id}>
                        {/* Bay rectangle */}
                        <rect
                          x={r.x} y={r.y} width={r.w} height={r.h}
                          fill={chargerFill}
                          stroke={isOverlap ? "#f97316" : chargerStroke}
                          strokeWidth={isOverlap ? 2.5 : 1.5}
                          rx={3}
                          filter="url(#charger-shadow)"
                          style={{ cursor: "grab", touchAction: "none" }}
                          onMouseDown={(e) => startDrag(e, "charger", c.id, c.x - bw / 2, c.y, bw, bl)}
                          onTouchStart={(e) => startDrag(e, "charger", c.id, c.x - bw / 2, c.y, bw, bl)}
                          onMouseEnter={(e) => handleHover({
                            label: `${c.id}${pi !== null ? ` (Unit ${pi + 1} ${isLeft ? "L" : "R"})` : ""}`,
                            details: [
                              (c.type || "DC Fast").replace(/_/g, " "),
                              `Bay: ${fmtLen(bw)}${unitLabel} × ${fmtLen(bl)}${unitLabel}`,
                              c.power_kw ? `${c.power_kw} kW per gun` : null,
                              "Drag to reposition",
                            ].filter(Boolean),
                          }, e)}
                          onMouseLeave={clearHover}
                        />
                        {/* Bay label */}
                        <text x-excluded="true"
                          x={r.x + r.w / 2} y={r.y + r.h / 2 + (r.h > 20 ? -3 : 4)}
                          textAnchor="middle" fill="white" fontSize={9} fontWeight="bold"
                          style={{ pointerEvents: "none" }}>
                          {c.id}
                        </text>
                        {r.h > 20 && (
                          <text x-excluded="true"
                            x={r.x + r.w / 2} y={r.y + r.h / 2 + 9}
                            textAnchor="middle" fill="rgba(255,255,255,0.88)" fontSize={8}
                            style={{ pointerEvents: "none" }}>
                            {c.power_kw ? `${c.power_kw}kW` : "⚡"}
                          </text>
                        )}
                        {/* Pedestal between paired bays — render once per pair (on left bay) */}
                        {pi !== null && isLeft && pair?.length === 2 && (() => {
                          const rightC = pair.find(x => x.pair_side === "right");
                          if (!rightC) return null;
                          const rR = toSvgRect(rightC.x - bw / 2, rightC.y, bw, bl);
                          const pX  = (r.x + r.w + rR.x) / 2;  // centre of gap
                          const pY  = r.y;                       // top of bays
                          const pW  = Math.max(4, rR.x - (r.x + r.w));
                          return (
                            <g x-excluded="true" style={{ pointerEvents: "none" }}>
                              {/* Pedestal post */}
                              <rect x={pX - pW / 2} y={pY} width={pW} height={bl * scaleY * 0.25}
                                fill="#1f2937" rx={2} />
                              {/* Cable lines */}
                              <line x1={r.x + r.w} y1={pY + 6} x2={pX - pW / 2} y2={pY + 6}
                                stroke="#1f2937" strokeWidth={1} strokeDasharray="2 2" />
                              <line x1={pX + pW / 2} y1={pY + 6} x2={rR.x} y2={pY + 6}
                                stroke="#1f2937" strokeWidth={1} strokeDasharray="2 2" />
                              {/* Unit label above */}
                              <text x-excluded="true" x={pX} y={pY - 4}
                                textAnchor="middle" fill="#475569" fontSize={8} fontWeight="600">
                                U{pi + 1}
                              </text>
                            </g>
                          );
                        })()}
                      </g>
                    );
                  });
                })()}
              </g>

              {/* Entry arrow */}
              <g>
                <polygon
                  points={`${MARGIN},${entryY - arrowSize} ${MARGIN + arrowSize * 1.6},${entryY} ${MARGIN},${entryY + arrowSize}`}
                  fill="#22c55e"
                />
                <text x={MARGIN + arrowSize * 1.8} y={entryY - 10}
                  fill="#22c55e" fontSize={9} fontWeight="700">ENTRY</text>
              </g>

              {/* Exit arrow */}
              <g>
                <polygon
                  points={`${SVG_W - MARGIN - arrowSize * 1.6},${exitY - arrowSize} ${SVG_W - MARGIN},${exitY} ${SVG_W - MARGIN - arrowSize * 1.6},${exitY + arrowSize}`}
                  fill="#ef4444"
                />
                <text x={SVG_W - MARGIN - arrowSize * 1.8 - 26} y={exitY - 10}
                  fill="#ef4444" fontSize={9} fontWeight="700">EXIT</text>
              </g>

              {/* X-axis ticks */}
              <g x-excluded="true">
                {xTicks.map(({ m, label }) => (
                  <g x-excluded="true" key={`xt${m}`}>
                    <line x1={toSvgX(m)} y1={SVG_H - MARGIN} x2={toSvgX(m)} y2={SVG_H - MARGIN + 4}
                      stroke="#94a3b8" strokeWidth={0.8} />
                    <text x-excluded="true"
                      x={toSvgX(m)} y={SVG_H - MARGIN + 13}
                      textAnchor="middle" fill="#64748b" fontSize={8}>{label}</text>
                  </g>
                ))}
              </g>

              {/* Y-axis ticks */}
              <g x-excluded="true">
                {yTicks.map(({ m, label }) => (
                  <g x-excluded="true" key={`yt${m}`}>
                    <line x1={MARGIN - 4} y1={toSvgY(m)} x2={MARGIN} y2={toSvgY(m)}
                      stroke="#94a3b8" strokeWidth={0.8} />
                    <text x-excluded="true"
                      x={MARGIN - 7} y={toSvgY(m) + 3}
                      textAnchor="end" fill="#64748b" fontSize={8}>{label}</text>
                  </g>
                ))}
              </g>

              {/* Axis dimension labels */}
              <text x-excluded="true"
                x={SVG_W / 2} y={SVG_H - 4}
                textAnchor="middle" fill="#94a3b8" fontSize={10}>
                Length ({fmtLen(siteW)}{unitLabel})
              </text>
              <text x-excluded="true"
                x={10} y={SVG_H / 2}
                textAnchor="middle" fill="#94a3b8" fontSize={10}
                transform={`rotate(-90, 10, ${SVG_H / 2})`}>
                Width ({fmtLen(siteH)}{unitLabel})
              </text>

              {/* Scale bar reference */}
              <g>
                <rect x={sbX - 3} y={sbY - 14} width={scaleBarPx + 6} height={17}
                  fill="#1e293b" fillOpacity={0.92} rx={2} />
                <line x1={sbX} y1={sbY} x2={sbX + scaleBarPx} y2={sbY}
                  stroke="#94a3b8" strokeWidth={1.5} strokeLinecap="round" />
                <line x1={sbX} y1={sbY - 4} x2={sbX} y2={sbY + 2} stroke="#94a3b8" strokeWidth={1.5} />
                <line x1={sbX + scaleBarPx} y1={sbY - 4} x2={sbX + scaleBarPx} y2={sbY + 2}
                  stroke="#94a3b8" strokeWidth={1.5} />
                <text x={sbX + scaleBarPx / 2} y={sbY - 6}
                  textAnchor="middle" fill="#94a3b8" fontSize={8}>{scaleBarLabel}</text>
              </g>

              {/* Legend */}
              <rect x={legendX - 6} y={legendY - 16} width={175} height={legendItems.length * 18 + 22}
                fill="#1e293b" fillOpacity={0.97} stroke="#334155" strokeWidth={1} rx={5} />
              <text x={legendX} y={legendY} fill="#e2e8f0" fontSize={10} fontWeight="700">Legend</text>
              <g x-excluded="true">
                {legendItems.map((item, i) => (
                  <g x-excluded="true" key={i}
                    transform={`translate(${legendX}, ${legendY + 10 + i * 18})`}>
                    <rect x={0} y={0} width={12} height={10}
                      fill={item.color} fillOpacity={item.opacity != null ? item.opacity : 1}
                      stroke={item.stroke != null ? item.stroke : item.color} strokeWidth={0.8}
                      strokeDasharray={item.dashed ? "4 2" : "none"} rx={1} />
                    <text x-excluded="true" x={16} y={9} fill="#cbd5e1" fontSize={9}>
                      {item.label}
                    </text>
                  </g>
                ))}
              </g>
            </svg>
          </div>
        </div>

        {/* ── Area info panel ──────────────────────────────────────────────── */}
        <div className="w-full lg:w-52 lg:flex-shrink-0 border border-slate-700 rounded-lg bg-slate-800/50 overflow-hidden">
          <div className="bg-slate-900/50 border-b border-slate-700 px-3 py-2">
            <h3 className="font-semibold text-slate-300 text-sm">Area Summary</h3>
          </div>
          <div className="p-3 space-y-3 text-sm overflow-y-auto" style={{ maxHeight: 590 }}>

            {/* Total site */}
            <div className="pb-2.5 border-b border-slate-800">
              <p className="text-xs text-slate-400 uppercase tracking-wide font-medium mb-1">Total Site</p>
              <p className="font-semibold text-slate-100">{fmtArea(siteArea)} {unitLabelSq}</p>
              <p className="text-xs text-slate-300 mb-1.5">{fmtLen(siteW)}{unitLabel} × {fmtLen(siteH)}{unitLabel}</p>
              <div className="w-full bg-slate-700 rounded-full h-1.5">
                <div
                  className="bg-blue-500 h-1.5 rounded-full transition-all"
                  style={{ width: `${Math.min(100, (usedArea / siteArea) * 100).toFixed(0)}%` }}
                />
              </div>
              <p className="text-xs text-slate-300 mt-0.5">
                {((usedArea / siteArea) * 100).toFixed(1)}% allocated
              </p>
            </div>

            {/* DC Fast Chargers */}
            {dcCount > 0 && (
              <div>
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: "#1d4ed8" }} />
                  <p className="font-medium text-slate-200 text-xs">DC Fast ({dcCount})</p>
                </div>
                <p className="text-slate-300 text-xs pl-4">{fmtArea(dcTotalArea)} {unitLabelSq} total</p>
                <p className="text-xs text-slate-300 pl-4">{fmtArea(dcTotalArea / dcCount)} {unitLabelSq} / bay</p>
              </div>
            )}

            {/* AC Slow Chargers */}
            {acCount > 0 && (
              <div>
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: "#16a34a" }} />
                  <p className="font-medium text-slate-200 text-xs">AC Slow ({acCount})</p>
                </div>
                <p className="text-slate-300 text-xs pl-4">{fmtArea(acTotalArea)} {unitLabelSq} total</p>
                <p className="text-xs text-slate-300 pl-4">{fmtArea(acTotalArea / acCount)} {unitLabelSq} / bay</p>
              </div>
            )}

            {/* Parking bays */}
            {parkingPositions.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0 bg-slate-300 border border-slate-400" />
                  <p className="font-medium text-slate-200 text-xs">Parking ({parkingPositions.length})</p>
                </div>
                <p className="text-slate-300 text-xs pl-4">{fmtArea(parkingTotalArea)} {unitLabelSq} total</p>
                <p className="text-xs text-slate-300 pl-4">
                  {fmtArea(parkingTotalArea / parkingPositions.length)} {unitLabelSq} / bay
                </p>
              </div>
            )}

            {/* Solar canopy */}
            {solarPos && (
              <div>
                <div className="flex items-center gap-1.5 mb-1">
                  <span
                    className="w-2.5 h-2.5 rounded-sm flex-shrink-0 border border-dashed border-green-500"
                    style={{ background: "rgba(0,200,83,0.2)" }}
                  />
                  <p className="font-medium text-slate-200 text-xs">Solar Canopy</p>
                </div>
                <p className="text-slate-300 text-xs pl-4">{fmtArea(solarArea)} {unitLabelSq}</p>
                <p className="text-xs text-slate-300 pl-4">
                  {fmtLen(solarPos.width)}{unitLabel} × {fmtLen(solarPos.height)}{unitLabel}
                </p>
                <p className="text-xs font-semibold text-green-400 pl-4">{computedSolarKwp} kWp</p>
              </div>
            )}

            {/* Amenity buildings */}
            {buildingEntries.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: "#f59e0b" }} />
                  <p className="font-medium text-slate-200 text-xs">Buildings ({buildingEntries.length})</p>
                </div>
                {buildingEntries.map(([btype, bdata]) => (
                  <div key={btype} className="pl-4 mb-0.5 flex items-center gap-1.5">
                    <span
                      className="w-2 h-2 rounded-sm flex-shrink-0"
                      style={{ background: getBuildingColor(btype) }}
                    />
                    <p className="text-xs text-slate-300">
                      {btype.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                      <span className="text-slate-300 ml-1">
                        {fmtArea(bdata.width * bdata.height)} {unitLabelSq}
                      </span>
                    </p>
                  </div>
                ))}
                <p className="text-xs text-slate-300 pl-4 mt-0.5">{fmtArea(totalBuildingArea)} {unitLabelSq} total</p>
              </div>
            )}

            {/* Unallocated */}
            <div className="pt-2 border-t border-slate-700">
              <p className="text-xs text-slate-400 uppercase tracking-wide font-medium mb-1">Unallocated</p>
              <p className="font-medium text-slate-200 text-xs">
                {fmtArea(Math.max(0, siteArea - usedArea))} {unitLabelSq}
              </p>
              <p className="text-xs text-slate-300">
                {(Math.max(0, 1 - usedArea / siteArea) * 100).toFixed(1)}% of site
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Hover tooltip ────────────────────────────────────────────────────── */}
      {hoveredEl && (
        <div
          className="absolute pointer-events-none bg-slate-900 text-white text-xs rounded px-3 py-2 shadow-xl z-50"
          style={{ left: hoveredEl.tipX, top: hoveredEl.tipY }}
        >
          <p className="font-bold mb-0.5">{hoveredEl.label}</p>
          {hoveredEl.details?.map((d, i) => (
            <p key={i} className="text-slate-300">{d}</p>
          ))}
        </div>
      )}
    </div>
  );
});

export default SiteLayoutMap;
